/**
 * @file WriteoffItemsList.tsx
 * @description Позиции списания: карточки на `<lg`, таблица на `≥lg`
 * (phase19.13 этап 0 — позиция = фасовка; колонки «Партия · срок» и «Причина
 * строки» — phase19.23, A2; единый шаблон документа — phase19.24 этап 1,
 * шаг 1).
 *
 * Колонки по утверждённому макету: «Партия», «Причина», «Кол-во». Ячейка
 * партии (`lot.bin`) переехала третьей строкой в колонку товара: у списания
 * она справочная — товар уже уходит со склада, а не ищется по адресу.
 *
 * «—» в ячейке означает не «нет данных», а «у партии не задан адрес хранения».
 */

import { Trash2 } from "lucide-react";

import { DocumentCard } from "../ui/DocumentCard";
import { DocumentItemsTable, type DocumentTableColumn } from "../ui/DocumentItemsTable";
import { RowActionsMenu, type RowAction } from "../ui/RowActionsMenu";
import { lotLabel } from "./document-texts";
import { WmsSkuThumb } from "./WmsSkuThumb";
import { formatSkuName, type WriteoffItemDto } from "../../lib/api-wms";

interface Props {
  items: WriteoffItemDto[];
  /** Строка, к которой увёл «Подбор» («уже добавлена») — подсветка. */
  highlightedId?: string | null;
  /** `data-row-id` строки для прокрутки к ней. */
  rowDomId?: (itemId: string) => string;
  /** Черновик — позиции можно удалять. */
  editable: boolean;
  onRemove: (itemId: string) => void;
}

/**
 * «L-240901 · до 01.12.2026» — партия и срок одной ячейкой таблицы.
 *
 * Партии нет вовсе — списываем по FEFO: партию выберет сервер при проведении.
 * Формат общий с приёмкой и перемещением (`lotLabel`).
 */
function lotText(item: WriteoffItemDto): string {
  if (!item.lot) return "Автовыбор (FEFO)";
  return lotLabel(item.lot.batchNumber, item.lot.expiresAt, "Автовыбор (FEFO)");
}

/** «Ячейка A-01-04» — адрес партии, из которой списываем. */
function binText(item: WriteoffItemDto): string {
  return `Ячейка: ${item.lot?.bin?.code ?? "—"}`;
}

export function WriteoffItemsList({
  items,
  highlightedId = null,
  rowDomId,
  editable,
  onRemove,
}: Props) {
  function rowActions(item: WriteoffItemDto) {
    const actions: RowAction[] = editable
      ? [
          {
            key: "remove",
            label: "Удалить позицию",
            icon: Trash2,
            danger: true,
            run: () => onRemove(item.id),
          },
        ]
      : [];
    return (
      <RowActionsMenu
        actions={actions}
        title={`Действия с позицией ${item.sku?.code ?? ""}`}
        ariaLabel="Действия с позицией"
        size="compact"
      />
    );
  }

  const columns: DocumentTableColumn<WriteoffItemDto>[] = [
    {
      key: "lot",
      label: "Партия",
      render: (item) => (
        <span className="font-mono text-xs text-on-surface-variant">{lotText(item)}</span>
      ),
    },
    {
      key: "reason",
      label: "Причина",
      render: (item) => (
        <span className="text-on-surface-variant">{item.notes ?? "—"}</span>
      ),
    },
    {
      key: "qty",
      label: "Кол-во",
      width: "72px",
      align: "right",
      render: (item) => <span className="tabular-nums">{item.quantity}</span>,
    },
  ];

  const emptyText = editable
    ? "Позиций пока нет. Добавьте первую через панель «Добавить строку» или отсканируйте артикул."
    : "Документ проведён без позиций.";

  return (
    <>
      {/* Черновик (рядом панель «Добавить строку») — таблица с 1280, как у
          приёмки: на 1024 колонке позиций остаётся ~370px (phase19.31). */}
      <ul className={editable ? "flex flex-col gap-2 xl:hidden" : "flex flex-col gap-2 lg:hidden"}>
        {/* Пустой черновик — та же подсказка, что у таблицы: на телефоне под
            «Позиции (0)» было пусто (дефект приёмки 2026-10-02). */}
        {items.length === 0 ? (
          <li className="rounded-xl bg-surface-lowest px-4 py-6 text-sm text-on-surface-variant ring-ghost">{emptyText}</li>
        ) : null}
        {items.map((item) => (
          <li key={item.id} data-row-id={rowDomId?.(item.id)}>
            <DocumentCard
              tone={highlightedId === item.id ? "selected" : "default"}
              media={<WmsSkuThumb />}
              title={formatSkuName(item.sku)}
              code={item.sku?.code ?? item.skuId}
              qty={item.quantity}
              meta={[
                lotText(item),
                binText(item),
                item.notes ? `Причина: ${item.notes}` : null,
              ].filter((row): row is string => !!row)}
              actions={editable ? rowActions(item) : undefined}
            />
          </li>
        ))}
      </ul>

      <DocumentItemsTable
        breakpoint={editable ? "xl" : "lg"}
        items={items}
        rowKey={(item) => item.id}
        rowId={rowDomId ? (item) => rowDomId(item.id) : undefined}
        rowTone={(item) => (highlightedId === item.id ? "selected" : "default")}
        columns={columns}
        media={() => <WmsSkuThumb />}
        title={(item) => formatSkuName(item.sku)}
        code={(item) => item.sku?.code ?? item.skuId}
        note={(item) => (
          <span className="text-xs text-on-surface-variant">{binText(item)}</span>
        )}
        actions={rowActions}
        emptyText={emptyText}
      />
    </>
  );
}
